import { create } from 'zustand';
import type { Folder, Prompt, PromptStore } from '../types/prompt';

const STORAGE_KEY = 'claudafinil_prompts';
const FOLDERS_STORAGE_KEY = 'claudafinil_folders';
const MAX_FOLDER_NAME_LENGTH = 50;

function generateId(): string {
  return crypto.randomUUID();
}

function persistPrompts(prompts: Prompt[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(prompts));
}

function loadPromptsFromStorage(): Prompt[] {
  const stored = localStorage.getItem(STORAGE_KEY);
  const prompts: Prompt[] = stored ? JSON.parse(stored) : [];

  let needsMigration = false;
  const migrated = prompts.map((prompt: Prompt & { folderId?: string | null }) => {
    if (prompt.folderId === undefined) {
      needsMigration = true;
      return { ...prompt, folderId: null };
    }

    return prompt;
  });

  if (needsMigration) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated));
  }

  return migrated;
}

function persistFolders(folders: Folder[]): void {
  localStorage.setItem(FOLDERS_STORAGE_KEY, JSON.stringify(folders));
}

function loadFoldersFromStorage(): Folder[] {
  const stored = localStorage.getItem(FOLDERS_STORAGE_KEY);
  return stored ? JSON.parse(stored) : [];
}

function validateFolderName(name: string, existingFolders: Folder[], excludeId?: string): string | null {
  const trimmed = name.trim();

  if (!trimmed) {
    return 'Folder name is required';
  }

  if (trimmed.length > MAX_FOLDER_NAME_LENGTH) {
    return `Folder name must be ${MAX_FOLDER_NAME_LENGTH} characters or less`;
  }

  const duplicate = existingFolders.some(
    (folder) => folder.id !== excludeId && folder.name.toLowerCase() === trimmed.toLowerCase(),
  );

  if (duplicate) {
    return 'A folder with this name already exists';
  }

  return null;
}

const initialState = {
  prompts: [] as Prompt[],
  folders: [] as Folder[],
  searchFilter: '',
  isLoading: false,
  error: null as string | null,
};

export const usePromptStore = create<PromptStore>((set, get) => ({
  ...initialState,

  loadPrompts: async () => {
    set({ isLoading: true, error: null });

    try {
      const prompts = loadPromptsFromStorage();
      set({ prompts, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load prompts';
      set({ error: message, isLoading: false });
    }
  },

  addPrompt: async (title, content, folderId = null) => {
    const now = Date.now();
    const newPrompt: Prompt = {
      id: generateId(),
      title,
      content,
      folderId,
      createdAt: now,
      updatedAt: now,
    };

    const updatedPrompts = [...get().prompts, newPrompt];

    try {
      persistPrompts(updatedPrompts);
      set({ prompts: updatedPrompts, error: null });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to add prompt';
      set({ error: message });
    }
  },

  updatePrompt: async (id, title, content, folderId) => {
    const updatedPrompts = get().prompts.map((prompt) => {
      if (prompt.id !== id) {
        return prompt;
      }

      const updates: Partial<Prompt> = {
        title,
        content,
        updatedAt: Date.now(),
      };

      if (folderId !== undefined) {
        updates.folderId = folderId;
      }

      return { ...prompt, ...updates };
    });

    try {
      persistPrompts(updatedPrompts);
      set({ prompts: updatedPrompts, error: null });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update prompt';
      set({ error: message });
    }
  },

  deletePrompt: async (id) => {
    const updatedPrompts = get().prompts.filter((prompt) => prompt.id !== id);

    try {
      persistPrompts(updatedPrompts);
      set({ prompts: updatedPrompts, error: null });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete prompt';
      set({ error: message });
    }
  },

  setSearchFilter: (filter) => {
    set({ searchFilter: filter });
  },

  loadFolders: async () => {
    set({ isLoading: true, error: null });

    try {
      const folders = loadFoldersFromStorage();
      set({ folders, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load folders';
      set({ error: message, isLoading: false });
    }
  },

  addFolder: async (name) => {
    const { folders } = get();

    const validationError = validateFolderName(name, folders);
    if (validationError) {
      set({ error: validationError });
      return null;
    }

    const newFolder: Folder = {
      id: generateId(),
      name: name.trim(),
      createdAt: Date.now(),
    };

    const updatedFolders = [...folders, newFolder];

    try {
      persistFolders(updatedFolders);
      set({ folders: updatedFolders, error: null });
      return newFolder.id;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to add folder';
      set({ error: message });
      return null;
    }
  },

  updateFolder: async (id, name) => {
    const { folders } = get();
    const validationError = validateFolderName(name, folders, id);

    if (validationError) {
      set({ error: validationError });
      return;
    }

    const updatedFolders = folders.map((folder) =>
      folder.id === id ? { ...folder, name: name.trim() } : folder,
    );

    try {
      persistFolders(updatedFolders);
      set({ folders: updatedFolders, error: null });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update folder';
      set({ error: message });
    }
  },

  deleteFolder: async (id) => {
    const { folders, prompts } = get();
    const updatedFolders = folders.filter((folder) => folder.id !== id);
    const updatedPrompts = prompts.filter((prompt) => prompt.folderId !== id);

    try {
      persistFolders(updatedFolders);
      persistPrompts(updatedPrompts);
      set({ folders: updatedFolders, prompts: updatedPrompts, error: null });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete folder';
      set({ error: message });
    }
  },
}));
